import { useCallback, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import type { JobPostingQueryRequest } from "@/services/api/job-postings";
import { useJobPostingFilters } from "../queries/useJobPostings";

const SEARCH_FIELD_OPTIONS = [
  { key: "Title", label: "Title" },
  { key: "Company", label: "Company" },
  { key: "Location", label: "Location" },
  { key: "Description", label: "Description" },
] as const;

const ALL_FIELD_KEYS = SEARCH_FIELD_OPTIONS.map((f) => f.key);

interface JobPostingsFiltersProps {
  filters: Partial<JobPostingQueryRequest>;
  onFilterChange: (
    key: keyof JobPostingQueryRequest,
    value: string | boolean | string[] | undefined
  ) => void;
  onClearFilters: () => void;
}

export function JobPostingsFilters({
  filters,
  onFilterChange,
  onClearFilters,
}: JobPostingsFiltersProps) {
  const { data: filterOptions } = useJobPostingFilters();

  const activeSearchFields = useMemo<string[]>(
    () => (filters.searchFields && filters.searchFields.length > 0 ? filters.searchFields : [...ALL_FIELD_KEYS]),
    [filters.searchFields]
  );

  const allFieldsActive = activeSearchFields.length === ALL_FIELD_KEYS.length;

  const placeholder = useMemo(() => {
    if (allFieldsActive) return "Search title, company, location, description\u2026";
    return `Search ${activeSearchFields.map((f) => f.toLowerCase()).join(", ")}\u2026`;
  }, [activeSearchFields, allFieldsActive]);

  const searchTerms = useMemo<string[]>(
    () => (Array.isArray(filters.searchTerm) ? filters.searchTerm : []),
    [filters.searchTerm]
  );

  const handleToggleField = useCallback(
    (field: string) => {
      const isActive = activeSearchFields.includes(field);
      if (isActive && activeSearchFields.length === 1) return;

      const next = isActive
        ? activeSearchFields.filter((f) => f !== field)
        : [...activeSearchFields, field];

      const sorted = ALL_FIELD_KEYS.filter((k) => next.includes(k));

      onFilterChange(
        "searchFields",
        sorted.length === ALL_FIELD_KEYS.length ? undefined : sorted
      );
    },
    [activeSearchFields, onFilterChange]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <SearchTermTagInput
          terms={searchTerms}
          onChange={(terms) =>
            onFilterChange("searchTerm", terms.length > 0 ? terms : undefined)
          }
          placeholder={placeholder}
        />

        <select
          value={filters.sourceName ?? ""}
          onChange={(e) =>
            onFilterChange("sourceName", e.target.value || undefined)
          }
          className="h-8 rounded-md border border-grid-200 bg-white px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">All Sources</option>
          {filterOptions?.sources.map((source) => (
            <option key={source} value={source}>
              {source}
            </option>
          ))}
        </select>

        <select
          value={filters.location ?? ""}
          onChange={(e) =>
            onFilterChange("location", e.target.value || undefined)
          }
          className="h-8 rounded-md border border-grid-200 bg-white px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">All Locations</option>
          {filterOptions?.locations.map((loc) => (
            <option key={loc} value={loc}>
              {loc}
            </option>
          ))}
        </select>

        <select
          value={filters.company ?? ""}
          onChange={(e) =>
            onFilterChange("company", e.target.value || undefined)
          }
          className="h-8 rounded-md border border-grid-200 bg-white px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">All Companies</option>
          {filterOptions?.companies.map((comp) => (
            <option key={comp} value={comp}>
              {comp}
            </option>
          ))}
        </select>

        <select
          value={
            filters.isActive !== undefined && filters.isActive !== null
              ? String(filters.isActive)
              : ""
          }
          onChange={(e) => {
            const val = e.target.value;
            onFilterChange(
              "isActive",
              val === "" ? undefined : val === "true"
            );
          }}
          className="h-8 rounded-md border border-grid-200 bg-white px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        {Object.keys(filters).length > 0 && (
          <button
            onClick={onClearFilters}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-ink-500 hover:bg-grid-100 hover:text-ink-700"
          >
            <X className="h-3 w-3" />
            Clear filters
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 pl-0.5">
        <span className="text-[11px] text-ink-400 mr-0.5">Search in:</span>
        {SEARCH_FIELD_OPTIONS.map(({ key, label }) => {
          const isActive = activeSearchFields.includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleToggleField(key)}
              className={`rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
                isActive
                  ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                  : "bg-grid-100 text-ink-400 hover:bg-grid-200 hover:text-ink-500"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SearchTermTagInput({
  terms,
  onChange,
  placeholder,
}: {
  terms: string[];
  onChange: (terms: string[]) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState("");

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed.length >= 2 && !terms.includes(trimmed)) {
      onChange([...terms, trimmed]);
    }
    setDraft("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && draft === "" && terms.length > 0) {
      onChange(terms.slice(0, -1));
    }
  };

  return (
    <div className="flex min-h-[32px] w-80 flex-wrap items-center gap-1 rounded-md border border-grid-200 bg-white px-2 py-1 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
      <Search className="h-4 w-4 shrink-0 text-ink-400" />
      {terms.map((t, i) => (
        <span key={i} className="contents">
          {i > 0 && (
            <span className="text-[10px] font-semibold uppercase text-blue-400">
              or
            </span>
          )}
          <span className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
            {t}
            <button
              type="button"
              onClick={() => onChange(terms.filter((_, idx) => idx !== i))}
              className="ml-0.5 hover:text-blue-900"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        placeholder={terms.length === 0 ? placeholder : "Add term…"}
        className="min-w-[60px] flex-1 border-none bg-transparent text-sm outline-none placeholder:text-ink-400"
      />
    </div>
  );
}
